/**
 * useBotShield — shared hook for every public lead form.
 *
 * Drops two anti-bot signals into the form data with zero UX cost:
 *
 *   1. Honeypot fields (`website`, `homepage`) — rendered via the
 *      <BotHoneypot/> component below, CSS-hidden and `aria-hidden`.
 *      Real users never see them; bots greedy-fill every input.
 *
 *   2. Submission timing — `submit_ms` captures how long the user
 *      took from form mount → submit. Bots typically fire <600 ms.
 *
 * Usage:
 *
 *   const shield = useBotShield();
 *   ...
 *   <BotHoneypot {...shield.fieldProps} />
 *   ...
 *   await fetch("/api/...", { body: JSON.stringify({
 *     email, ...shield.values()
 *   }) });
 *
 * No external deps, no captcha, no cookies.
 */
import { useEffect, useRef, useState } from "react";

export function useBotShield() {
  const mountedAt = useRef(Date.now());
  const [website, setWebsite] = useState("");
  const [homepage, setHomepage] = useState("");

  // Reset the timer if the consumer wants to (e.g. after a reset).
  const reset = () => { mountedAt.current = Date.now(); setWebsite(""); setHomepage(""); };

  return {
    fieldProps: {
      website,
      homepage,
      onWebsite: setWebsite,
      onHomepage: setHomepage,
    },
    values: () => ({
      website,
      homepage,
      submit_ms: Date.now() - mountedAt.current,
    }),
    reset,
  };
}

/**
 * BotHoneypot — visually hidden honeypot fields. Bots fill these; humans
 * don't see them. Uses positioning rather than display:none so screen
 * readers and accessibility tools can still announce them as "decoy" —
 * but we set aria-hidden and tabIndex=-1 to keep real users away.
 */
export function BotHoneypot({ website, homepage, onWebsite, onHomepage }) {
  // useEffect keeps the React state matched even if a bot fills via the DOM
  // (which some headless drivers do).
  useEffect(() => { /* no-op — values are kept in React state already */ }, []);
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        left: "-9999px",
        top: "auto",
        width: 1,
        height: 1,
        overflow: "hidden",
        opacity: 0,
        pointerEvents: "none",
      }}
    >
      <label>
        Website (leave blank)
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => onWebsite?.(e.target.value)}
        />
      </label>
      <label>
        Homepage (leave blank)
        <input
          type="text"
          name="homepage"
          tabIndex={-1}
          autoComplete="off"
          value={homepage}
          onChange={(e) => onHomepage?.(e.target.value)}
        />
      </label>
    </div>
  );
}
